/**
 * @file JS - Class - MultiMapPage.js
 * @author : Adam Lemire
 * @since 29/07/2020
 * @version 1.0
 * @summary Dernières modifications : 11/02/2022
 */

import Page, { PageType } from './Page.js';
import { EPSG, MultiMapPageFormat } from '../../helpers/Constants.js'; // eslint-disable-line no-unused-vars
import SingleMapPage from './SingleMapPage.js'; // eslint-disable-line no-unused-vars
import View from 'ol/View.js';

const COORDONNEES_CENTRE = [-8000000, 6000000];

export default class MultiMapPage extends Page {
  /**
   * @example new MultiMapPage( { id: pageId, title: pageName } )
   * @param { object } options - Options du constructeur
   * @param { number } options.id - Id de la carte
   * @param { string } options.title - Titre de la carte
   * @param { MultiMapPageFormat } options.format
   * @param { object } options.jsonMultiMapPage - Seulement lorsque créé par l'ouverture d'un projet existant
   */
  constructor ({ id, title, format = MultiMapPageFormat.HORIZONTAL, jsonMultiMapPage = false }) {
    if (jsonMultiMapPage) {
      super(jsonMultiMapPage.id, jsonMultiMapPage.title, PageType.MULTIMAPPAGE);
      this._createFromJSON(jsonMultiMapPage);
    } else {
      super(id, title, PageType.MULTIMAPPAGE);
      /** @type { MultiMapPageFormat } */
      this._format = format;
      /** @type { Map <number, SingleMapPage> } - position, SingleMapPage */
      this._maps = new Map();
      /**
       * @type { View }
       * @description toutes les cartes d'une MultiMapPage ont la même view
       */
      this._view = new View({
        projection: EPSG.default,
        center: COORDONNEES_CENTRE,
        zoom: 6,
        constrainResolution: true
      });
    }
  }

  get format () { return this._format; }

  /** @param { MultiMapPageFormat } format */
  set format (format) {
    this._manageMapsPositionsFromFormat(format);
    this._format = format;
  }

  get maps () { return this._maps; }

  /** @param { Map <number, SingleMapPage> } maps */
  set maps (maps) {
    this._maps = maps;
  }

  /** @param { MultiMapPageFormat } format */
  _manageMapsPositionsFromFormat (format) {
    if (this._format === MultiMapPageFormat.OVERLAY) {
      const baseMap = this.getMapAtPosition(5);
      const overlayMap = this.getMapAtPosition(6);

      baseMap && this.setMapAtPosition(baseMap, 1);
      overlayMap && this.setMapAtPosition(overlayMap, 2);
      this._maps.set(5, undefined);
      this._maps.set(6, undefined);
    }

    if (format === MultiMapPageFormat.OVERLAY) {
      const baseMap = this.getMapAtPosition(1);
      const overlayMap = this.getMapAtPosition(2);

      baseMap && this.setMapAtPosition(baseMap, 5);
      overlayMap && this.setMapAtPosition(overlayMap, 6);
      this._maps.set(1, undefined);
      this._maps.set(2, undefined);
    }
  }

  /**
   * @param { SingleMapPage } singleMapPage
   * @param { number } position
   */
  setMapAtPosition (singleMapPage, position) {
    this._maps.set(position, singleMapPage);
    const target = 'multimap-' + this.id + '__map' + position;
    singleMapPage.changeTarget(target);
    console.log('setMapAtPosition : %o', position);
    singleMapPage.setTemporaryView(this._view);
  }

  /**
   * @param { number } position
   * @returns { SingleMapPage }
   */
  getMapAtPosition (position) {
    return this._maps.get(position);
  }

  /**
   * @param { SingleMapPage } singleMapPage
   * @returns { number }
   */
  getMapPosition (singleMapPage) {
    let position = -1;
    this._maps.forEach(function (value, key) {
      if (value && singleMapPage.id === value.id) position = key;
    });
    return position;
  }

  /** @param { number } position */
  enleverCarteDePosition (position) {
    this._maps.set(position, undefined);
  }

  zoomIn () {
    this._maps.forEach(function (singleMapPage, position) {
      if (singleMapPage) {
        const view = singleMapPage.olMap.getView();
        view.animate({
          zoom: view.getZoom() + 1,
          duration: 200
        });
      }
    });
  }

  zoomOut () {
    this._maps.forEach(function (singleMapPage, position) {
      if (singleMapPage) {
        const view = singleMapPage.olMap.getView();
        view.animate({
          zoom: view.getZoom() - 1,
          duration: 200
        });
      }
    });
  }

  centerMap () {
    this._maps.forEach(function (singleMapPage, position) {
      if (singleMapPage) {
        singleMapPage.olMap.getView().setCenter(COORDONNEES_CENTRE);
      }
    });
  }

  /** @override */
  refreshView () {
    this._notifyViewChanged();
    this._maps.forEach(function (singleMapPage, position) {
      singleMapPage && singleMapPage.refreshMap();
    });
  }

  _notifyViewChanged () {}

  registerViewChangedListener (listener) {
    this._notifyViewChanged = listener;
  }

  /*
   * JSON SPECIFIC - Exportation en JSON
   */

  /** @override */
  toJSON () {
    return {
      id: this.id,
      title: this.title,
      pageType: this.pageType,
      format: this._format,
      maps: this._getMapsAsJSON(),
      view: this._getViewAsJSON()
    };
  }

  _getMapsAsJSON () {
    const jsonMaps = [];

    for (const [key, value] of this._maps) {
      if (value) jsonMaps.push({ position: key, singleMapPageId: value.id });
    }
    return jsonMaps;
  }

  _getViewAsJSON () {
    const view = {
      center: {
        x: this._view.getCenter()[0],
        y: this._view.getCenter()[1]
      },
      zoom: this._view.getZoom()
    };
    return view;
  }

  /*
   * JSON - depuis ouverture projet (les cartes se réajoutent dans le fichier : ? (fonctionne))
   */
  _createFromJSON (jsonMultiMapPage) {
    /** @type { MultiMapPageFormat } */
    this._format = jsonMultiMapPage.format || MultiMapPageFormat.HORIZONTAL;
    /** @type { Map <number, SingleMapPage> } */
    this._maps = new Map();
    /** @type { View } */
    this._view = new View({
      projection: EPSG.default,
      center: [jsonMultiMapPage.view.center.x, jsonMultiMapPage.view.center.y],
      zoom: jsonMultiMapPage.view.zoom,
      constrainResolution: true
    });
  }
}
